import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { isValidEmail } from '@seedlang/utils';
import Modal from 'components/modal';
import EmailTypoWarning from 'components/form/email_typo_warning';
import { ReportBugWrapper } from '@seedlang/hoc';
import styled from '@emotion/styled';
import Text from 'components/text';
import InPlaceTextInput from 'components/form/in_place_text_input';
import InPlaceImageUpload from 'components/form/in_place_image_upload';
import Button from 'components/button/button';
import { Theme } from '@seedlang/constants';
import Alert from 'components/alert';
import { DeckUI, AppUI, ContactEmailState } from '@seedlang/state';

const Wrapper = styled.div`
  background: #545454;
  height: 100%;
  padding: 10px 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  margin: 10px 0 0 0;
`;

@observer
class ReportBug extends React.Component {

  @observable emailFocused = false;
  emailChange = new ContactEmailState({
    getEmail: () => this.props.email,
    setEmail: email => this.props.setEmail(email),
    canChangeAccountEmail: () => this.canChangeAccountEmail,
  });

  @computed get canChangeAccountEmail() {
    return AppUI.siteIsSeedlang && AppUI.userIsSignedInAndNotGuest && !AppUI.user.confirmed;
  }

  @computed get showEmailInput() {
    return this.props.requiresEmailAddress || this.canChangeAccountEmail;
  }

  @computed get disabled() {
    return this.props.disabled || this.props.submitting || this.emailChange.saving ||
      (this.showEmailInput && !isValidEmail(this.props.email));
  }

  componentDidMount() {
    if (this.canChangeAccountEmail && !this.props.email) this.props.setEmail(AppUI.user.email);
  }

  onEmailChange = value => this.emailChange.changeEmail(value);

  onEmailBlur = () => { this.emailFocused = false; };

  onSubmit = () => {
    if (this.disabled) return;
    if (this.showEmailInput) this.emailChange.submit(this.sendMessage);
    else this.sendMessage();
  };

  sendMessage = () => {
    if (this.disabled) return;
    this.props.onSubmit({webVersion: DeckUI.user?.site?.currentWebVersion});
  };

  onReset = () => {
    this.props.onReset();
    this.onEmailChange(this.canChangeAccountEmail ? AppUI.user.email : '');
    this.emailChange.showModal = false;
    this.emailChange.pendingSubmit = null;
  };


  render() {
    return (
      <Wrapper>
        {this.emailChange.showModal &&
          <Modal
            title='Change Your Account Email?'
            background={Theme.blue}
            width='100%'
            maxWidth='560px'
            onCloseModal={this.emailChange.cancel}
          >
            <p>Would you also like to change your Seedlang account’s email address from <strong>{AppUI.user.email}</strong> to <strong>{this.props.email.trim().toLowerCase()}</strong>?</p>
            <EmailTypoWarning email={this.props.email} />
            {this.emailChange.error && <Alert background={Theme.red} role='alert'>{this.emailChange.error}</Alert>}
            <ButtonRow style={{ justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <Button background={Theme.blue} disabled={this.emailChange.saving} onClick={this.emailChange.save}>
                {this.emailChange.saving ? 'Updating…' : 'Update Account Email'}
              </Button>
              <Button background={Theme.gray} disabled={this.emailChange.saving} onClick={this.emailChange.finish}>Use for This Message Only</Button>
            </ButtonRow>
          </Modal>
        }
        {this.emailChange.sentTo &&
          <Alert background={Theme.green} role='status'>
            Your account email has been updated. We sent a confirmation email to {this.emailChange.sentTo}.
          </Alert>
        }
        {
          this.props.displaySubmittedMessage &&
            <Alert>
              Thank you for submitting your bug report. We will be in touch shortly.
            </Alert>
        }
        <Text
          heading='2'
          textAlign='center'
          padding='0 0 20px 0'
          color='#FFF'
        >
          Report Bug
        </Text>
        <Text
          padding='10px 0'
          color='#FFF'
          textAlign='left'
          fontSize='14px'
          lineHeight='18px'
        >
          Thank you for letting us know about the problem that you are experiencing. This message will include information such as your device type and your deck's current state.
        </Text>
        {
          this.showEmailInput &&
            <Text
              padding='10px 0'
              color='#FFF'
              textAlign='left'
              bold
            >
              Email
            </Text>
        }
        {
          this.showEmailInput &&
            <InPlaceTextInput
              type='email'
              value={this.props.email}
              onChange={this.onEmailChange}
              onBlur={this.onEmailBlur}
              onFocus={() => this.emailFocused = true}
            />
        }
        {this.showEmailInput && <EmailTypoWarning email={this.props.email} focused={this.emailFocused} />}
        {this.showEmailInput && !this.emailFocused && this.props.email && !isValidEmail(this.props.email) &&
          <Alert background={Theme.red} role='alert'>Please enter a valid email address.</Alert>
        }
        <Text
          padding='10px 0'
          color='#FFF'
          textAlign='left'
          bold
        >
          Message
        </Text>
        <InPlaceTextInput
          value={this.props.message}
          inputType='textarea'
          onChange={val => this.props.setMessage(val)}
          height='140px'
          expandedHeight='140px'
        />
        <Text
          padding='10px 0'
          color='#FFF'
          textAlign='left'
          bold
        >
          Attach Image
        </Text>
        <InPlaceImageUpload
          model='images'
          uploadUrl='/api/contact_messages/upload_image'
          margin='0'
          onChange={this.props.setImage}
          image={this.props.image}
        />
        <Text
          padding='10px 0'
          color='#FFF'
          textAlign='left'
          fontSize='14px'
        >
          If you can attach a screenshot, it is very helpful for troubleshooting.
        </Text>
        <ButtonRow>
          <Button
            background={Theme.green}
            color='#FFF'
            width={130}
            disabled={this.disabled}
            onClick={this.onSubmit}
            margin='0 10px 0 0'
          >
            Send
          </Button>
          <Button
            background={Theme.red}
            color='#FFF'
            text='Reset'
            width={130}
            marginLeft={10}
            disabled={this.disabled}
            onClick={this.onReset}
          >
            Reset
          </Button>
        </ButtonRow>
        {
          DeckUI.currentCard && !DeckUI.currentCard.translation?.simple &&
            <Text
              padding='10px 0'
              color='#FFF'
              textAlign='left'
              fontSize='14px'
              lineHeight='18px'
            >
              Note: If you would like to report a problem with a translation, please report it by clicking on the translations or post it to our comments section.
              &nbsp;<span
                className='underline'
                onClick={() => DeckUI.gotoCardComments(DeckUI.currentCard)}
              >
                Open Comments Section
              </span>
            </Text>
        }
      </Wrapper>
    );
  }
}

export default ReportBugWrapper(ReportBug);
